/**
 * `useTaskUpdates` — live subscription to ALL work-log updates on
 * a single task (across all days).
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/tasks/useTaskUpdates.ts
 *
 * Path: `tasks/{taskId}/updates/{updateId}`. Sort: newest first
 * (matches the per-day `useProjectTaskUpdatesForDate` hook used on
 * the Site tab).
 */
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/lib/firebase';
import type { TaskUpdateDoc } from '@/lib/useProjectTaskUpdatesForDate';

export type UseTaskUpdatesResult = {
  data: TaskUpdateDoc[];
  loading: boolean;
};

export function useTaskUpdates(
  taskId: string | undefined,
): UseTaskUpdatesResult {
  const [data, setData] = useState<TaskUpdateDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!taskId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      collection(db, 'tasks', taskId, 'updates'),
      (snap) => {
        const rows: TaskUpdateDoc[] = snap.docs.map((d) => {
          const v = d.data() as Omit<TaskUpdateDoc, 'id'>;
          return {
            id: d.id,
            authorId: v.authorId ?? '',
            authorName: v.authorName ?? 'Member',
            progress: typeof v.progress === 'number' ? v.progress : 0,
            text: v.text ?? '',
            photoUris: Array.isArray(v.photoUris) ? v.photoUris : [],
            createdAt: v.createdAt ?? null,
          };
        });
        rows.sort((a, b) => {
          const at = a.createdAt?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
          const bt = b.createdAt?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
          return bt - at;
        });
        setData(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useTaskUpdates] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [taskId]);

  return { data, loading };
}
